import { useState, useEffect } from 'react';
import { X, Gift, Copy, Check } from 'lucide-react';
import { trackButtonClick } from '../../utils/analytics';

const DISMISS_KEY = 'marketing_popup_dismissed_at';

export function MarketingPopup({ content, theme, goToSignup }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const config = content?.marketing_popup || {};
  const enabled = config.enabled === true;
  const triggerOnDelay = config.trigger_on_delay !== false; // default true (legacy behaviour)
  const triggerOnScroll = config.trigger_on_scroll === true;
  const scrollThresholdPct = Math.max(50, Math.min(100, parseInt(config.scroll_threshold_pct) || 90));
  const delaySec = parseInt(config.delay_seconds);
  const delay = (Number.isFinite(delaySec) && delaySec > 0) ? delaySec : 20;
  const resetDaysParsed = parseInt(config.reset_days);
  const resetDays = Number.isFinite(resetDaysParsed) ? resetDaysParsed : 7;
  const heading = config.heading || 'Special Offer Just For You';
  const description = config.description || 'Sign up today and save on your first month.';
  const code = config.code || '';
  const buttonText = config.button_text || 'Claim Offer';
  const dismissText = config.dismiss_text || 'Maybe later';

  // Common gate: shouldn't show when recently dismissed
  const isSuppressed = () => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return false;
    const elapsed = Date.now() - parseInt(dismissedAt);
    const resetMs = resetDays * 24 * 60 * 60 * 1000;
    return elapsed < resetMs;
  };

  // Time-based trigger
  useEffect(() => {
    if (!enabled || !triggerOnDelay) return;
    if (isSuppressed()) return;
    const timer = setTimeout(() => setVisible(true), delay * 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, triggerOnDelay, delay, resetDays]);

  // Scroll-to-bottom trigger — fires once per session when the user passes
  // `scrollThresholdPct` of the page. Stored in sessionStorage so a tab return
  // doesn't refire it; localStorage dismiss still suppresses across sessions.
  useEffect(() => {
    if (!enabled || !triggerOnScroll) return;
    if (isSuppressed()) return;
    if (sessionStorage.getItem('marketing_popup_scroll_fired') === '1') return;

    const onScroll = () => {
      const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );
      const scrolled = window.innerHeight + window.scrollY;
      const pct = (scrolled / docHeight) * 100;
      if (pct >= scrollThresholdPct) {
        sessionStorage.setItem('marketing_popup_scroll_fired', '1');
        setVisible(true);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount in case the page is already at the bottom
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, triggerOnScroll, scrollThresholdPct, resetDays]);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback ignored
    }
  };

  const handleClaim = () => {
    trackButtonClick('marketing_popup_claim', buttonText);
    close();
    if (goToSignup) goToSignup();
  };

  if (!visible) return null;

  const isDark = theme === 'dark';

  return (
    <>
      <div className="fixed inset-0 z-[99] bg-black/20 backdrop-blur-[1px]" onClick={close} data-testid="marketing-popup-backdrop" />
      <div
        className="fixed left-0 right-0 bottom-0 z-[100] px-4 py-4 sm:px-6"
        data-testid="marketing-popup"
      >
        <div
          className={`max-w-4xl mx-auto rounded-2xl border shadow-2xl ${
            isDark
              ? 'bg-gray-900/95 border-gray-700 backdrop-blur-md'
              : 'bg-white/95 border-gray-200 backdrop-blur-md'
          }`}
        >
          <div className="p-5 sm:p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg ${isDark ? 'bg-[#007a33]/20' : 'bg-[#007a33]/10'}`}>
                  <Gift className="w-4 h-4 text-[#007a33]" />
                </div>
                <h3 className={`font-semibold text-base ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="marketing-popup-heading">
                  {heading}
                </h3>
              </div>
              <button
                onClick={close}
                className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                data-testid="marketing-popup-close-btn"
                aria-label="Close offer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Description */}
            <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-testid="marketing-popup-description">
              {description}
            </p>

            {/* Discount code */}
            {code && (
              <div
                className={`mb-5 rounded-xl border px-4 py-3 flex items-center justify-between gap-3 ${
                  isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}
                data-testid="marketing-popup-code-row"
              >
                <div className="min-w-0">
                  <p className={`text-[10px] uppercase tracking-wide font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Use code</p>
                  <p className={`font-mono text-lg font-bold tracking-wider truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="marketing-popup-code-value">
                    {code}
                  </p>
                </div>
                <button
                  onClick={handleCopy}
                  className={`shrink-0 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                    copied
                      ? 'bg-[#007a33] text-white'
                      : isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                  }`}
                  data-testid="marketing-popup-copy-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
              <button
                onClick={handleClaim}
                className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#007a33] hover:bg-[#005c26] text-white transition-colors"
                data-testid="marketing-popup-claim-btn"
              >
                {buttonText}
              </button>
              <button
                onClick={close}
                className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isDark
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="marketing-popup-dismiss-btn"
              >
                {dismissText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
